/**
 * Record-list report mapping: GraphQL entity nodes → flat report rows.
 *
 * Record-list blocks (`src/data/reports/recordList.ts`) render a *spec* — an object,
 * a column list, filters, grouping and sort — against rows keyed by the catalog keys
 * in `DEAL_FIELDS` / `COMPANY_FIELDS` / `PEOPLE_FIELDS`. Preview builds those rows from
 * `@/data/dummy`; production builds them here from the same `getDeals` / `getAccounts` /
 * `getContacts` queries the grids use. Both paths therefore emit **the same row keys and
 * value shapes**, so the filter/sort/group/format engine and the whole render path are
 * identical in both modes.
 *
 * Everything in this file is pure — `now` is injected so age/duration math is
 * deterministic in tests, and no hook or environment check appears below.
 *
 * FIELD MAPPING (report row key ⇄ GraphQL source)
 *
 * Deals — `Deal` node from `getDeals` (`DealFields` + `DealRelations`):
 *   name             ⇄ properties.name
 *   company          ⇄ accounts.edges[0].node.properties.name (else .domain)
 *   stage            ⇄ properties.stage, decoded to a label via ctx.stageNames
 *   amount           ⇄ properties.amount
 *   closeDate        ⇄ properties.closeDate                       (MMM d, yyyy)
 *   owner            ⇄ owner (UserFields), preferring ctx.memberNames
 *   forecastCategory ⇄ properties.forecastCategory
 *   probability      ⇄ ctx.stagePct[stage] (see note), else properties.probability
 *   contacts         ⇄ contacts.edges[].node first+last name, comma-joined
 *   createdDate      ⇄ properties.createDate (falls back to asOfTs, as the grid does)
 *   stageEnteredDate ⇄ inStageSince
 *   stageDuration    ⇄ days between inStageSince and now
 *   age              ⇄ days between createDate and now
 *   lastEmail        ⇄ lastEmail.startTime
 *   lastMeeting      ⇄ lastActivityEngagement.startTime
 *   nextMeeting      ⇄ nextStepEngagements[0].startTime
 *   custom_<name>    ⇄ customProperties[<name>]
 *   billing, createdBy, industry, country, region → NO SOURCE (see
 *   PRODUCTION_UNSUPPORTED_FIELDS)
 *
 * Companies — `Account` node from `getAccounts` (`AccountFields` + `AccountEngagements`):
 *   name          ⇄ properties.name (else domain)
 *   website       ⇄ domain
 *   industry      ⇄ properties.industry
 *   employees     ⇄ properties.employeeCountMin/Max as a range label,
 *                   else properties.numberOfEmployees
 *   revenue       ⇄ properties.revenueMin/Max as a $ range label,
 *                   else properties.annualRevenue
 *   owner         ⇄ owner (UserBaseFields), preferring ctx.memberNames
 *   city          ⇄ properties.city
 *   stateProvince ⇄ properties.state
 *   country       ⇄ properties.country
 *   postalCode    ⇄ properties.postalCode
 *   continent     ⇄ properties.continent
 *   companyType   ⇄ properties.entityType
 *   funding       ⇄ properties.raisedAmount
 *   foundingYear  ⇄ properties.foundedYear (numeric when parseable)
 *   phone         ⇄ properties.phone
 *   description   ⇄ properties.description (else aboutUs)
 *   custom_<name> ⇄ customProperties[<name>]
 *   primaryContact, primaryContactEmail, openDeals, openPipeline → NO SOURCE
 *
 * People — `Contact` node from `getContacts` (`ContactFields` + `ContactEngagements`):
 *   name          ⇄ properties.firstName + properties.lastName (else primaryEmail)
 *   title         ⇄ properties.jobTitle
 *   company       ⇄ accounts.edges[0].node.properties.name (else .domain)
 *   email         ⇄ primaryEmail
 *   phone         ⇄ properties.phone
 *   location      ⇄ properties.city, state, country joined
 *   owner         ⇄ owner (UserBaseFields), preferring ctx.memberNames
 *   lastEmail     ⇄ lastEmail.startTime
 *   lastMeeting   ⇄ lastEngagement.startTime
 *   nextMeeting   ⇄ nextEngagement.startTime
 *   custom_<name> ⇄ customProperties[<name>]
 *   createdDate → NO SOURCE (Contact exposes only asOfTs, a *last seen* timestamp)
 *
 * Probability note: `DealProperties.probability` is a Float whose scale is not pinned
 * down anywhere in the frontend (backend stage probabilities are 0–1, the UI shows
 * 0–100). The stage percentage from the workspace's pipeline is authoritative and
 * matches what preview shows, so it wins; a raw value ≤ 1 is treated as a fraction.
 */

import { differenceInDays, format } from "date-fns";

import { displayNumber } from "@/lib/i18n/display";

import type {
  AccountFilter,
  ContactFilterInput,
  DealFilter,
} from "@/lib/graphql/types-and-hooks";
import type { RecordListSpec, RecordObject } from "@/data/reports/recordList";

/** Placeholder the record-list engine treats as "empty" (see `matches`/`formatCell`). */
const DASH = "-";
const DATE_FMT = "MMM d, yyyy";

// ---------------------------------------------------------------------------
// Structural node types
// ---------------------------------------------------------------------------
// Deliberately structural rather than the generated `Deal`/`Account`/`Contact`
// types: the mappers only read a handful of leaves, and structural inputs let the
// unit tests build a two-field fixture instead of a full GraphQL node.

export interface OwnerLike {
  id?: string | null;
  givenName?: string | null;
  preferredName?: string | null;
  surname?: string | null;
  email?: string | null;
}

interface EngagementLike {
  startTime?: string | null;
}

interface AccountRefLike {
  id?: string | null;
  domain?: string | null;
  properties?: { name?: string | null } | null;
}

interface EdgesLike<T> {
  edges?: ({ node?: T | null } | null)[] | null;
}

export interface DealNodeLike {
  id?: string | null;
  asOfTs?: string | null;
  inStageSince?: string | null;
  customProperties?: Record<string, unknown> | null;
  properties?: {
    name?: string | null;
    amount?: number | null;
    closeDate?: string | null;
    createDate?: string | null;
    stage?: string | null;
    probability?: number | null;
    forecastCategory?: string | null;
  } | null;
  owner?: OwnerLike | null;
  accounts?: EdgesLike<AccountRefLike> | null;
  contacts?: EdgesLike<{
    primaryEmail?: string | null;
    properties?: { firstName?: string | null; lastName?: string | null } | null;
  }> | null;
  lastEmail?: EngagementLike | null;
  lastActivityEngagement?: EngagementLike | null;
  nextStepEngagements?: (EngagementLike | null)[] | null;
}

export interface AccountNodeLike {
  id?: string | null;
  domain?: string | null;
  customProperties?: Record<string, unknown> | null;
  properties?: {
    name?: string | null;
    industry?: string | null;
    numberOfEmployees?: number | string | null;
    employeeCountMin?: number | null;
    employeeCountMax?: number | null;
    annualRevenue?: string | null;
    revenueMin?: number | null;
    revenueMax?: number | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    postalCode?: string | null;
    continent?: string | null;
    entityType?: string | null;
    raisedAmount?: string | null;
    foundedYear?: number | string | null;
    phone?: string | null;
    description?: string | null;
    aboutUs?: string | null;
  } | null;
  owner?: OwnerLike | null;
  lastEmail?: EngagementLike | null;
  lastEngagement?: EngagementLike | null;
  nextEngagement?: EngagementLike | null;
}

export interface ContactNodeLike {
  id?: string | null;
  primaryEmail?: string | null;
  customProperties?: Record<string, unknown> | null;
  properties?: {
    firstName?: string | null;
    lastName?: string | null;
    jobTitle?: string | null;
    phone?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
  } | null;
  owner?: OwnerLike | null;
  accounts?: EdgesLike<AccountRefLike> | null;
  lastEmail?: EngagementLike | null;
  lastEngagement?: EngagementLike | null;
  nextEngagement?: EngagementLike | null;
}

export interface RecordListMappingContext {
  /** Raw backend stage value (id / externalId / name) → display label. */
  stageNames?: Map<string, string>;
  /** Stage display label → win probability as a 0–100 percentage. */
  stagePct?: Map<string, number>;
  /** Workspace member id → display name, so owners read the same as in the grids. */
  memberNames?: Map<string, string>;
  /** Injected clock for age / stage-duration math. */
  now?: Date;
}

// ---------------------------------------------------------------------------
// Unsupported fields
// ---------------------------------------------------------------------------

/**
 * Catalog fields the production queries cannot fill.
 *
 * These stay in the preview catalog (the dummy rows do carry them) but are hidden
 * from the production column picker and stripped from specs, so a real user never
 * sees a column that is structurally "-" — or worse, a plausible-looking value with
 * no source behind it.
 *
 * Evidence, field by field:
 * - `deals.billing` — there is no `billing` anywhere on `DealProperties`; the Deals
 *   grid hardcodes `billing: '-'`.
 * - `deals.createdBy` — no creator is exposed on `Deal`. The Deals grid fills the
 *   column with the *owner*, which is a different person whenever a record changes
 *   hands, so a report must not repeat that claim.
 * - `deals.industry` / `deals.country` — would have to come from the deal's account,
 *   but `getDeals` embeds accounts via `AccountBaseFields` (id, domain, name, logo,
 *   avatar background) only.
 * - `deals.region` — derived from `country`, which is itself unavailable.
 * - `companies.primaryContact` / `companies.primaryContactEmail` — `getAccounts`
 *   fetches no contacts connection.
 * - `companies.openDeals` / `companies.openPipeline` — `getAccounts` fetches no deals
 *   connection and no per-account aggregate.
 * - `people.createdDate` — `Contact` has no create timestamp in the schema (only
 *   `asOfTs`, a last-seen marker). The People grid reads a `createTs` field that the
 *   query never selects, so that column is already always "-".
 */
export const PRODUCTION_UNSUPPORTED_FIELDS: Record<RecordObject, Set<string>> = {
  deals: new Set(["billing", "createdBy", "industry", "country", "region"]),
  companies: new Set(["primaryContact", "primaryContactEmail", "openDeals", "openPipeline"]),
  people: new Set(["createdDate"]),
};

export const isFieldSupportedInProduction = (object: RecordObject, key: string): boolean =>
  !PRODUCTION_UNSUPPORTED_FIELDS[object].has(key);

/**
 * Strip everything the production data path cannot back out of a spec.
 *
 * Columns, filters, grouping and sort all go through the same check, so a saved
 * report authored in preview degrades to its supported subset rather than rendering
 * blank columns or silently filtering on a field that is always empty. The saved spec
 * itself is untouched — this only shapes what gets rendered — so the dropped pieces
 * come back if the backend later grows a source for them.
 */
export function sanitizeSpecForProduction(spec: RecordListSpec): RecordListSpec {
  const ok = (key?: string) => !key || isFieldSupportedInProduction(spec.object, key);
  return {
    ...spec,
    columns: spec.columns.filter(ok),
    filters: spec.filters.filter(f => ok(f.field)),
    groupBy: ok(spec.groupBy) ? spec.groupBy : undefined,
    sortBy: ok(spec.sortBy) ? spec.sortBy : undefined,
  };
}

// ---------------------------------------------------------------------------
// Shared value helpers
// ---------------------------------------------------------------------------

const toDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDate = (value?: string | null): string => {
  const d = toDate(value);
  return d ? format(d, DATE_FMT) : DASH;
};

const daysSince = (value: Date | null, now: Date): number =>
  value ? Math.max(0, differenceInDays(now, value)) : 0;

const text = (value?: string | null): string => {
  const v = (value ?? "").trim();
  return v || DASH;
};

/** Owner display name, preferring the workspace-member list so reports match the grids. */
export function ownerDisplayName(owner: OwnerLike | null | undefined, ctx: RecordListMappingContext): string {
  if (!owner) return DASH;
  const mapped = owner.id ? ctx.memberNames?.get(String(owner.id)) : undefined;
  if (mapped) return mapped;
  const composed = [owner.preferredName || owner.givenName, owner.surname]
    .filter(Boolean)
    .join(" ")
    .trim();
  return composed || owner.email || DASH;
}

/** `customProperties` → `custom_<name>` row keys, matching every grid transform. */
const customEntries = (props?: Record<string, unknown> | null): Record<string, unknown> =>
  props ? Object.fromEntries(Object.entries(props).map(([k, v]) => [`custom_${k}`, v])) : {};

const firstNode = <T>(conn?: EdgesLike<T> | null): T | undefined =>
  conn?.edges?.map(e => e?.node).find((n): n is T => Boolean(n));

/**
 * Range label for min/max pairs ("5K to 10K", "$1M+", "Under $500").
 *
 * Mirrors coffee-ui's `NumberUtils.getRangeValue`, which `CompaniesPage` and
 * `CompanyDetailSheet` each inline today — reports must read identically to the
 * Companies grid, hence the same algorithm. (Consolidating the three copies is a
 * separate cleanup; doing it here would drag two large pages into this change.)
 */
export function formatRangeValue(min?: number | null, max?: number | null, prefix = ""): string {
  const pretty = (n: number) => {
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2).replace(/\.?0+$/, "")}B`;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(2).replace(/\.?0+$/, "")}K`;
    return displayNumber(n);
  };

  const numMin = Number(min);
  const numMax = Number(max);
  const over = (n: number) => (n > 100_000 ? `${prefix}${pretty(n)} and Over` : `${prefix}${pretty(n)}+`);

  if (min && !max) return over(numMin);
  if (min && max) {
    if (numMax < numMin || Math.abs(numMax - numMin) < 1e-10) return over(numMin);
    return `${prefix}${pretty(numMin)} to ${prefix}${pretty(numMax)}`;
  }
  if (!min && max) return `Under ${prefix}${pretty(numMax)}`;
  return DASH;
}

// ---------------------------------------------------------------------------
// Node → row
// ---------------------------------------------------------------------------

export function dealNodeToRow(node: DealNodeLike, ctx: RecordListMappingContext = {}): Record<string, any> {
  const now = ctx.now ?? new Date();
  const account = firstNode(node.accounts);
  const contacts = (node.contacts?.edges ?? [])
    .map(e => e?.node)
    .filter(Boolean)
    .map(c =>
      `${c!.properties?.firstName ?? ""} ${c!.properties?.lastName ?? ""}`.trim() || c!.primaryEmail || "",
    )
    .filter(Boolean);

  const rawStage = node.properties?.stage ?? "";
  const stage = ctx.stageNames?.get(rawStage) || rawStage || DASH;

  // Stage percentage is the same 0–100 number preview shows; a raw deal probability
  // stored as a 0–1 fraction is scaled up so the column never reads "0" for a 50% deal.
  const rawProbability = node.properties?.probability;
  const probability =
    ctx.stagePct?.get(stage) ??
    (rawProbability == null ? 0 : rawProbability > 0 && rawProbability <= 1 ? Math.round(rawProbability * 100) : rawProbability);

  const createDate = toDate(node.properties?.createDate) ?? toDate(node.asOfTs);
  const inStageSince = toDate(node.inStageSince);

  return {
    id: node.id,
    name: text(node.properties?.name),
    company: text(account?.properties?.name || account?.domain),
    stage,
    amount: node.properties?.amount ?? 0,
    closeDate: fmtDate(node.properties?.closeDate),
    owner: ownerDisplayName(node.owner, ctx),
    forecastCategory: text(node.properties?.forecastCategory),
    probability,
    contacts: contacts.join(", ") || DASH,
    createdDate: createDate ? format(createDate, DATE_FMT) : DASH,
    stageEnteredDate: inStageSince ? format(inStageSince, DATE_FMT) : DASH,
    stageDuration: daysSince(inStageSince, now),
    age: daysSince(createDate, now),
    lastEmail: fmtDate(node.lastEmail?.startTime),
    lastMeeting: fmtDate(node.lastActivityEngagement?.startTime),
    nextMeeting: fmtDate(node.nextStepEngagements?.[0]?.startTime),
    ...customEntries(node.customProperties),
  };
}

export function accountNodeToRow(node: AccountNodeLike, ctx: RecordListMappingContext = {}): Record<string, any> {
  const p = node.properties;
  const employees = formatRangeValue(p?.employeeCountMin, p?.employeeCountMax);
  const revenue = formatRangeValue(p?.revenueMin, p?.revenueMax, "$");
  const foundedYear = Number(p?.foundedYear);

  return {
    id: node.id,
    name: text(p?.name || node.domain),
    website: text(node.domain),
    industry: text(p?.industry),
    employees: employees !== DASH ? employees : text(p?.numberOfEmployees != null ? String(p.numberOfEmployees) : null),
    revenue: revenue !== DASH ? revenue : text(p?.annualRevenue),
    owner: ownerDisplayName(node.owner, ctx),
    city: text(p?.city),
    stateProvince: text(p?.state),
    country: text(p?.country),
    postalCode: text(p?.postalCode),
    continent: text(p?.continent),
    companyType: text(p?.entityType),
    funding: text(p?.raisedAmount),
    foundingYear: Number.isFinite(foundedYear) && foundedYear > 0 ? foundedYear : DASH,
    phone: text(p?.phone),
    description: text(p?.description || p?.aboutUs),
    lastEmail: fmtDate(node.lastEmail?.startTime),
    lastMeeting: fmtDate(node.lastEngagement?.startTime),
    nextMeeting: fmtDate(node.nextEngagement?.startTime),
    ...customEntries(node.customProperties),
  };
}

export function contactNodeToRow(node: ContactNodeLike, ctx: RecordListMappingContext = {}): Record<string, any> {
  const p = node.properties;
  const account = firstNode(node.accounts);
  const name = `${p?.firstName ?? ""} ${p?.lastName ?? ""}`.trim() || node.primaryEmail || DASH;
  const location = [p?.city, p?.state, p?.country].filter(Boolean).join(", ");

  return {
    id: node.id,
    name,
    title: text(p?.jobTitle),
    company: text(account?.properties?.name || account?.domain),
    email: text(node.primaryEmail),
    phone: text(p?.phone),
    location: text(location),
    owner: ownerDisplayName(node.owner, ctx),
    lastEmail: fmtDate(node.lastEmail?.startTime),
    lastMeeting: fmtDate(node.lastEngagement?.startTime),
    nextMeeting: fmtDate(node.nextEngagement?.startTime),
    ...customEntries(node.customProperties),
  };
}

// ---------------------------------------------------------------------------
// Spec → server filter
// ---------------------------------------------------------------------------

export interface ServerFilterContext {
  workspaceId: string;
  /** Lowercased owner display name → numeric user id (`DealFilter.ownerId` is Int). */
  memberIds?: Map<string, string>;
  /**
   * Lowercased stage label → the value `DealFilter.stage` term-matches, which is the
   * stage's `externalId` (the backend indexes `properties.stage.value` as that id).
   */
  stageExternalIds?: Map<string, string>;
}

export type ServerFilter = DealFilter | AccountFilter | ContactFilterInput;

const numericOwnerIds = (values: string[], ctx: ServerFilterContext): number[] | null => {
  const ids: number[] = [];
  for (const v of values) {
    const id = ctx.memberIds?.get(v.trim().toLowerCase());
    const n = id == null ? NaN : Number(id);
    // A name we cannot resolve must abort the pushdown: filtering on the resolvable
    // subset would silently drop rows the client-side filter would have kept.
    if (!Number.isFinite(n)) return null;
    ids.push(n);
  }
  return ids.length ? ids : null;
};

/**
 * Translate the parts of a record-list spec the fixed-field filter inputs can express.
 *
 * Only *narrowing* pushdowns are emitted, and only for `is` equality (plus numeric and
 * date ranges on deals). The client-side engine re-applies every filter afterwards, so
 * a pushdown can only ever reduce how much data crosses the wire — it can never change
 * the result. Anything the inputs cannot express (contains, is-not, custom fields,
 * account-side attributes) is simply left to the client filter.
 */
export function specToServerFilter(spec: RecordListSpec, ctx: ServerFilterContext): ServerFilter {
  const base = { workspaceId: ctx.workspaceId, deleted: false };

  const owners = spec.filters.find(f => f.field === "owner" && f.op === "is" && f.values.length > 0);
  const ownerId = owners ? numericOwnerIds(owners.values, ctx) : null;
  const withOwner = ownerId ? { ...base, ownerId } : base;

  if (spec.object !== "deals") return withOwner as AccountFilter | ContactFilterInput;

  const filter: DealFilter = { ...withOwner };

  const stageFilter = spec.filters.find(f => f.field === "stage" && f.op === "is" && f.values.length > 0);
  if (stageFilter) {
    const ids = stageFilter.values.map(v => ctx.stageExternalIds?.get(v.trim().toLowerCase()));
    if (ids.every((id): id is string => Boolean(id))) filter.stage = ids;
  }

  for (const f of spec.filters) {
    const bound = f.values[0];
    if (!bound) continue;

    if (f.field === "amount" && (f.op === "greater than" || f.op === "less than")) {
      // Strip currency formatting, but a value with no digits at all ("soon") must not
      // become `Number("") === 0` and silently push a `> 0` bound.
      const digits = String(bound).replace(/[^0-9.-]/g, "");
      const n = Number(digits);
      if (!digits || !Number.isFinite(n)) continue;
      filter.amount = { ...filter.amount, [f.op === "greater than" ? "gt" : "lt"]: n };
    }

    if (f.field === "closeDate" && (f.op === "greater than" || f.op === "less than")) {
      const d = toDate(bound);
      if (!d) continue;
      filter.closeDate = { ...filter.closeDate, [f.op === "greater than" ? "gt" : "lt"]: d.toISOString() };
    }
  }

  return filter;
}
